import React, { useCallback, useEffect, useMemo } from 'react';
import { ContactListOutlined, SubtractUniversal } from '@duxton-web/icons';
import { Button, Form, Tooltip, Typography } from '@duxton-web/ui';
import { useQueries } from '@tanstack/react-query';
import { PageHeaderInfo } from '@web-platform/page-header';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { styled } from 'styled-components';

import {
  ExperimentAction,
  getAssignByFacetFromDomain,
  getVariables,
  isEditMode,
  isViewMode,
} from '../constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '../contexts/experiment-context';
import { Experiment, RegionalExperimentConfigValues } from '../domain';
import {
  cloneExperiment,
  createExperiment,
  setAdaptiveExperiment,
  setAnalysis,
  setConfiguration,
  setExperiment,
  setVariables,
  updateConfiguration,
  updateExperiment,
  updateValidExperiment,
  updateValidMetric,
} from '../store/actions/experiment/action-creators';
import { parseConfiguration, parseExperiment } from '../utils';
import ExperimentMetadata from './experiment-metadata';
import Footer from './footer';
import SetupForm from './setup-form';
import ValidateExperiment from './validate-experiment';
import ViewModeContent from './view-mode-content';
import { ObjectDetailPage } from '@/common/components';
import { ABIcon } from '@/common/Icons/ABIcon';
import { AdaptiveIcon } from '@/common/Icons/AdaptiveIcon';
import Loader from '@/components/loader';
import { queryKeys } from '@/hooks/constants';
import { useGetAnalysis } from '@/hooks/usePlannerQueries';
import Service from '@/lib/App';
import { Variable, VariableInfo } from '@/lib/PlannerDTOs';
import {
  TreatmentDefaultColors,
  TreatmentIds,
} from '@/pages/ExperimentFormV2/helper/consts';
import { AdaptiveDetailDrawer } from '@/pages/ExperimentPageV2/AdaptiveDetailSubPage/AdaptiveDetail';
import { getStatusTagConfig } from '@/pages/ExperimentPageV2/common/DashboardColumn';
import { appendEnvParam } from '@/utils/navigation';

export const extractTreatmentData = (experiment: Experiment) => {
  const treatmentCount = Math.max(
    ...(experiment?.variables?.map((variable) => variable.choices.length) || [
      0,
    ]),
  );

  const treatmentIndexes = Array.from({ length: treatmentCount }, (_, i) => i);

  return treatmentIndexes.map((index) => {
    const treatmentId = TreatmentIds[index];
    return {
      id: treatmentId,
      name:
        treatmentId === 'Control' ? treatmentId : `Treatment ${treatmentId}`,
      color: TreatmentDefaultColors[treatmentId],
    };
  });
};

const ExperimentDetail = () => {
  const params = useParams<{ action: string; experimentName: string }>();
  const { experimentName: paramExperimentName, action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const {
    currentExperiment,
    experiment,
    currentAdaptiveExperiment,
    adaptiveExperiment,
    configurations,
  } = experimentContext;

  const mainExperiment = experiment || adaptiveExperiment;
  const experimentDispatch = useExperimentDispatchContext();
  const variables = getVariables(configurations);
  const shouldFetchExperiment =
    (currentExperiment?.name !== paramExperimentName &&
      currentAdaptiveExperiment?.name !== paramExperimentName) ||
    action === ExperimentAction.CLONE;

  const experimentQueries = useQueries({
    queries: [
      {
        queryKey: queryKeys.planner.experiment(paramExperimentName || ''),
        queryFn: () => Service.planner.getExperiment(paramExperimentName || ''),
        enabled: !!paramExperimentName && shouldFetchExperiment,
        retry: false,
      },
      {
        queryKey: queryKeys.planner.adaptiveExperiment(
          paramExperimentName || '',
        ),
        queryFn: () =>
          Service.planner.getAdaptiveExperiment(paramExperimentName || ''),
        enabled: !!paramExperimentName && shouldFetchExperiment,
        retry: false,
      },
    ],
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.fetchStatus === 'fetching'),
      };
    },
  });

  useEffect(() => {
    if (!experimentQueries.isLoading) {
      const responses = experimentQueries.data.filter(
        (experiment) => experiment,
      );
      responses?.forEach((res) => {
        if (res?.experiment) {
          let resp = res.experiment;
          if (isEditMode(action)) {
            if (resp?.name) {
              resp = {
                ...resp,
                name: resp.name.replace(`${resp.domain}.`, ''),
              };
            }
          }
          if (action === ExperimentAction.CLONE)
            experimentDispatch(cloneExperiment(resp, false, null));
          else experimentDispatch(setExperiment(resp, false, null));

          // When cloning, remove salt and id from variables
          const configurations =
            action === ExperimentAction.CLONE
              ? res.regional_experiment_config_values?.map(
                  (config: RegionalExperimentConfigValues) => ({
                    ...config,
                    variables: config.variables?.map((variable: any) => {
                      const {
                        salt,
                        experimentStartTime,
                        sliceAssignments,
                        ...rest
                      } = variable;
                      return {
                        ...rest,
                        choices: rest.choices?.map((choice: any) => {
                          const { id, ...choiceRest } = choice;
                          return choiceRest;
                        }),
                      };
                    }),
                  }),
                )
              : res.regional_experiment_config_values;

          experimentDispatch(setConfiguration(configurations));
          experimentDispatch(updateValidExperiment(true));
          experimentDispatch(updateValidMetric(true));
        }

        if (res?.adaptive_experiment) {
          experimentDispatch(setAdaptiveExperiment(res, false, null));
        }
      });
    } else {
      experimentDispatch(setExperiment(null, true, null));
      experimentDispatch(setConfiguration([]));
      experimentDispatch(setAdaptiveExperiment(null, true, null));
    }
  }, [experimentQueries.isLoading, experimentQueries.data, action]);

  const {
    data: analysisData,
    isLoading: isLoadingAnalysis,
    error: errorAnalysis,
  } = useGetAnalysis(paramExperimentName || '', {
    enabled: !!paramExperimentName,
  });

  // Extract variable names from the experiment data
  const variableNames = useMemo(() => {
    if (!configurations?.length) return [];

    return variables?.map((v: Variable) => v.name) || [];
  }, [experiment?.name]);

  // Fetch all variables in parallel
  const variableQueries = useQueries({
    queries: variableNames.map((variableName: string) => ({
      queryKey: queryKeys.planner.variable(variableName),
      queryFn: () => Service.planner.getVariable(variableName),
      enabled: variableNames.length > 0,
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        isLoading: results.some((result) => result.fetchStatus === 'fetching'),
      };
    },
  });

  const { color, content, tooltip } = getStatusTagConfig(
    experiment?.state || adaptiveExperiment?.state,
  );

  const [form] = Form.useForm();
  form.setFieldsValue({
    ...experiment,
    configurations: configurations,
  });

  useEffect(() => {
    if (action === ExperimentAction.CREATE) {
      if (experiment) return;

      const newExperiment: Experiment = { name: '' };

      experimentDispatch(
        createExperiment({ experiment: newExperiment }, false, null),
      );
    }
  }, [action]);

  useEffect(() => {
    if (paramExperimentName) {
      experimentDispatch(
        setAnalysis(analysisData?.AnalysisV2, isLoadingAnalysis, errorAnalysis),
      );
    }
  }, [analysisData, isLoadingAnalysis, errorAnalysis, paramExperimentName]);

  // Handle the loaded variables
  useEffect(() => {
    if (!variableQueries.isLoading && variableQueries.data.length > 0) {
      experimentDispatch(
        setVariables(
          variableQueries?.data
            ?.map((data: any) => {
              return data?.data;
            })
            .reduce((acc: VariableInfo[], curr: VariableInfo) => {
              return [...acc, curr];
            }, []),
        ),
      );
    }
  }, [variableQueries.isLoading, variableQueries.data]);

  function getExperimentInfo(): PageHeaderInfo[] {
    let domain =
      experiment?.domain || adaptiveExperiment?.experiment_template?.domain;
    let productManager = experiment?.metadata?.productManager || '';
    let productAnalyst = experiment?.metadata?.productAnalyst || '';
    const assignByFacet = getAssignByFacetFromDomain(domain);

    let infos = [
      {
        title: 'Created by',
        content: mainExperiment?.owner,
      },
      {
        title: 'Type',
        content: 'A/B test',
      },
      {
        title: 'Domain',
        content: domain,
      },
    ];

    if (assignByFacet) {
      infos.push({
        title: 'Assign by',
        content: assignByFacet.title,
      });
    }

    if (productManager)
      infos.push({
        title: 'Product manager',
        content: productManager,
      });

    if (productAnalyst)
      infos.push({
        title: 'Product analyst',
        content: productAnalyst,
      });

    return infos;
  }

  function showPrimaryActions() {
    if (readOnly) return null;

    return [
      <>
        <ExperimentMetadata />
        <Button
          icon={<ContactListOutlined />}
          onClick={() => {
            window.open(
              'https://techdocs.grab.com/grabx/grabx-user-guide/experiments/experiments_how-to-create-an-experiment/',
              '_blank',
            );
          }}
          size="small"
          type="secondary"
          shape="circle"
        />
      </>,
    ];
  }

  function showSecondaryActions() {
    if (readOnly) return null;

    return [<ValidateExperiment key="validate-experiment" />];
  }

  const debouncedHandleFormValuesChange = useCallback(
    _.debounce(async (changedValues: any, allFields: any) => {
      if (changedValues?.configurations) {
        const nextConfigurations = parseConfiguration(changedValues, allFields);

        experimentDispatch(updateConfiguration(nextConfigurations));
      }

      if (!changedValues?.configurations) {
        const { configurations, ...experiment } = form.getFieldsValue(true);

        const nextExperiment = parseExperiment(changedValues, experiment);

        experimentDispatch(updateExperiment(nextExperiment));
      }

      form
        .validateFields({ validateOnly: true })
        .then(() => {
          experimentDispatch(updateValidExperiment(true));
        })
        .catch((_e) => {
          experimentDispatch(updateValidExperiment(false));
        });
    }, 500),
    [],
  );

  const handleFormValuesChange = (changedValues: any, allFields: any) => {
    debouncedHandleFormValuesChange(changedValues, allFields);
  };

  if (adaptiveExperiment?.name)
    return <AdaptiveDetailDrawer adaptiveExperiment={adaptiveExperiment} />;

  if (
    experimentQueries.isLoading ||
    variableQueries.isLoading ||
    isLoadingAnalysis
  )
    return <Loader />;

  return (
    <Form
      form={form}
      preserve
      initialValues={{
        ...currentExperiment,
        configurations: [...configurations],
      }}
      onValuesChange={handleFormValuesChange}
      style={{
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <ObjectDetailPage
        icon={adaptiveExperiment?.name ? <AdaptiveIcon /> : <ABIcon />}
        breadcrumbs={[
          {
            path: appendEnvParam(`/t/experiments`),
            title: 'Experiment',
          },
          {
            path: '',
            title: experiment?.name,
          },
        ]}
        descriptionLabel="Hypothesis"
        description={
          experiment?.metadata?.description ? (
            <Tooltip
              title={experiment.metadata.description}
              overlayStyle={{ maxWidth: 600 }}
            >
              <Typography.Text
                ellipsis
                style={{ maxWidth: '60vw', color: '#A3A3A3', fontSize: 12 }}
              >
                {experiment.metadata.description}
              </Typography.Text>
            </Tooltip>
          ) : (
            <SubtractUniversal />
          )
        }
        infos={getExperimentInfo()}
        footer={<Footer />}
        headerTags={[
          {
            color: color,
            content: content,
            tooltip: tooltip,
            bordered: false,
            size: 'xsmall',
          },
        ]}
        primaryActions={showPrimaryActions()}
        secondaryActions={showSecondaryActions()}
      >
        <ViewModeContent />
        {!readOnly && <SetupForm />}
      </ObjectDetailPage>
    </Form>
  );
};

export const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

export const FormBody = styled.div`
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px;
`;

export const FormSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  isolation: isolate;
  z-index: 1;
`;

export default ExperimentDetail;
